import { useMemo } from 'react';
import { Glyphicon, Button } from 'react-bootstrap';
import { GridWrapper } from './styles';
import { AgGridReact } from 'ag-grid-react';
import { GridReadyEvent, IHeaderParams, ICellRendererParams, ValueGetterParams, CellClassParams, FirstDataRenderedEvent, ColDef, RowDataChangedEvent } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { IDisplaySetting, IInProcessSwap } from './models';
import { IMoveTracker } from '@nkr/models/index';

interface IProps {
    swapList: IDisplaySetting[],
    onShowSwap: (chainId: number, clusterNumber: number, positionNumber: number) => void,
    onShowAllSwaps: (show: boolean) => void
}

const SwapList = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const columns: ColDef[] = [
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>#</SortableHeaderComponent>,
            width: 40,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
            cellStyle: (params: CellClassParams) => {
                let data = params.data as IDisplaySetting;

                return {
                    backgroundColor: data.color
                };
            }
        },

        {
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    <div style={{ marginTop: 0 }}>Show</div>
                </SortableHeaderComponent>,
            width: 80,
            cellRendererParams: { onShowSwap: props.onShowSwap },
            cellRendererFramework: (params: ICellRendererParams & { onShowSwap: (chainId: number, clusterNumber: number, positionNumber: number) => void }) => {
                let data = params.data as IDisplaySetting;

                return (
                    <input
                        type='checkbox'
                        checked={data.show}
                        onClick={() => params.onShowSwap(data.chainId, data.clusterNumber, data.positionNumber)}
                    />
                );
            }
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap #</SortableHeaderComponent>,
            width: 80,
            cellRendererFramework: (params: ICellRendererParams) => `${params.data.chainId}/${params.data.clusterNumber}/${params.data.positionNumber}`
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Center</SortableHeaderComponent>,
            width: 175,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMoveTracker;

                if (data.rdnCenterId > 0) {
                    return <>{data.donorCenter} ({data.rdnCenterAlias})</>;
                } else {
                    return <>{data.donorCenter}</>;
                }
            }
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Scheduled<br />Picked</SortableHeaderComponent>,
            field: 'kidneyPickup',
            width: 135,
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Center</SortableHeaderComponent>,
            field: 'recipientCenter',
            width: 175,
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Scheduled<br />Delivery</SortableHeaderComponent>,
            field: 'kidneyArrival',
            width: 135,
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Courier</SortableHeaderComponent>,
            field: 'courier',
            width: 80,
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Transport<br />Method</SortableHeaderComponent>,
            field: 'logistics.transportMethod',
            width: 140,
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Pickup</SortableHeaderComponent>,
            width: 130,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMoveTracker;

                if (data.pickup.complete) {
                    return <>{data.pickup.actualDateTime}</>;
                } else {
                    return <>{data.pickup.scheduleDateTime}</>;
                }
            }
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Flight 1</SortableHeaderComponent>,
            width: 130,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMoveTracker;
                let transportMethod = data.logistics.transportMethod.toUpperCase();

                if (['GROUND'].indexOf(transportMethod) < 0) {
                    if (data.flightDeparture2.complete) {
                        return <>{data.flightDeparture1.actualDateTime}</>;
                    } else {
                        return <>{data.flightDeparture1.scheduleDateTime}</>;
                    }
                } else {
                    return <>N/A</>;
                }
            }
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Flight 2</SortableHeaderComponent>,
            width: 130,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMoveTracker;
                let transportMethod = data.logistics.transportMethod.toUpperCase();

                if (['GROUND', 'COMMERCIAL', 'CHARTER', 'COMMONELEGOBC'].indexOf(transportMethod) < 0) {
                    if (data.flightDeparture2.complete) {
                        return <>{data.flightDeparture2.actualDateTime}</>;
                    } else {
                        return <>{data.flightDeparture2.scheduleDateTime}</>;
                    }
                } else {
                    return <>N/A</>;
                }
            }
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Airline Recovery</SortableHeaderComponent>,
            width: 130,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMoveTracker;
                let transportMethod = data.logistics.transportMethod.toUpperCase();

                if (['GROUND'].indexOf(transportMethod) < 0) {
                    if (data.airlineRecovery.complete) {
                        return <>{data.airlineRecovery.actualDateTime}</>;
                    } else {
                        return <>{data.airlineRecovery.scheduleDateTime}</>;
                    }
                } else {
                    return <>N/A</>;
                }
            }
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Delivery</SortableHeaderComponent>,
            width: 130,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMoveTracker;

                if (data.delivery.complete) {
                    return <>{data.delivery.actualDateTime}</>;
                } else {
                    return <>{data.delivery.scheduleDateTime}</>;
                }
            }
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>View<br />Beacons</SortableHeaderComponent>,
            width: 85,
            cellRendererFramework: (params: ICellRendererParams) => {
                return (
                    <a href={`/beacon-overlay-map?s=${params.data.chainId}/${params.data.clusterNumber}/${params.data.positionNumber}`} target='_blank'>View</a>
                );
            }
        }
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.swapList, [props.swapList]);

    return (
        <>
            <div className='text-right'>
                <Button bsStyle='link' bsSize='xs' style={{ outline: 0 }} onClick={() => props.onShowAllSwaps(true)}><Glyphicon glyph='check' style={{ marginRight: 3, color: '#ddd' }} /> Check All</Button>
                <Button bsStyle='link' bsSize='xs' style={{ outline: 0 }} onClick={() => props.onShowAllSwaps(false)}><Glyphicon glyph='unchecked' style={{ color: '#ddd' }} /> Uncheck All</Button>
            </div>

            <GridWrapper className='ag-theme-bootstrap'>
                <AgGridReact
                    columnDefs={columnDefs}
                    defaultColDef={{
                        cellClass: 'swap-list-cell',
                        headerClass: 'swap-list-header',
                    }}
                    rowData={rowData}
                    headerHeight={43}
                    suppressRowClickSelection={false}
                    suppressCellSelection={true}
                    immutableData={true}
                    getRowNodeId={(data: IInProcessSwap) => data.chainId + '-' + data.clusterNumber + '-' + data.positionNumber}
                    noRowsOverlayComponentFramework={() => <div></div>}
                    onGridReady={onGridReady}
                    onFirstDataRendered={onFirstDataRendered}
                    onRowDataChanged={onRowDataChanged}
                    getRowStyle={(params: any) => {
                        if (params.data.activeLogisticsPlan == 2)
                            return {
                                backgroundColor: '#660000',
                                color: 'white'
                            };
                    }}
                />
            </GridWrapper>
        </>
    );
}

export default SwapList;